import React from "react";
import { CardContent, Stack, Typography, Box } from "@mui/material";
import {
  VideoLibrary as VideoIcon,
  FitnessCenter as FitnessCenterIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import moment from "moment";

import {
  VideoCard,
  VideoThumbnail,
  ThumbnailContainer,
  StatusChip,
  TypeChip,
  PlayIconOverlay,
} from "./StyledComponents";

const SharedVideo = React.memo(({ video, onClick, thumbnail }) => {
  return (
    <VideoCard isProcessed={video.processed} onClick={() => onClick(video)}>
      <VideoThumbnail isProcessed={video.processed}>
        <ThumbnailContainer>
          {/* Display generated thumbnail if available */}
          {thumbnail ? (
            <Box
              component="img"
              src={thumbnail}
              alt="Video thumbnail"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "black",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          )}

          {/* Play button overlay */}
          <PlayIconOverlay />

          {/* Status indicator */}
          <StatusChip
            icon={video.processed ? <CheckIcon /> : <ScheduleIcon />}
            label={video.processed ? "Processed" : "Review"}
            size="small"
            color={video.processed ? "success" : "warning"}
            sx={{
              bgcolor: video.processed
                ? "rgba(76, 175, 80, 0.9)"
                : "rgba(255, 152, 0, 0.9)",
              color: "white",
            }}
          />

          {/* Video type indicator */}
          <TypeChip
            icon={
              video.metadata?.exercise ? <FitnessCenterIcon /> : <VideoIcon />
            }
            label={
              video.metadata?.exercise ? "Exercise Video" : "General Video"
            }
            size="small"
            color={video.metadata?.exercise ? "primary" : "secondary"}
            sx={{
              bgcolor: video.metadata?.exercise
                ? "rgba(25, 118, 210, 0.9)"
                : "rgba(156, 39, 176, 0.9)",
              color: "white",
            }}
          />
        </ThumbnailContainer>

        <CardContent sx={{ p: 2 }}>
          {/* Notes (truncated) */}
          {video.metadata?.notes || video.notes ? (
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <DescriptionIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {video.metadata?.notes || video.notes}
              </Typography>
            </Stack>
          ) : null}

          <Stack spacing={1}>
            {/* Date information */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon fontSize="small" color="disabled" />
              <Typography variant="caption" color="text.secondary" noWrap>
                {moment(video.createdOn.toDate()).format("MMM D, YYYY h:mm A")}
              </Typography>
            </Stack>

            {/* Upload timeframe */}
            <Stack direction="row" justifyContent="flex-end">
              <Typography variant="caption" color="text.secondary">
                {moment(video.createdOn.toDate()).fromNow()}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </VideoThumbnail>
    </VideoCard>
  );
});

SharedVideo.displayName = "SharedVideo";

export default SharedVideo;
